import { later, next, schedule } from '@ember/runloop';
import EmberService, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature, SourceStatusLevel, SourceTypeCategory } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';

import BoardPopupPreviewComponent from 'webapp/components/board/popup-preview';
import CommonPopupConfirmComponent from 'webapp/components/common/popups/confirm';
import CommonPopupsPopupBlueBubble from 'webapp/components/common/popups/popup-blue-bubble';
import DataManagerPopupSelectedConnectionsComponent from 'webapp/components/data-manager/popup-selected-connections';
import OnboardingGuidedTourPopupEndComponent from 'webapp/components/onboarding/guided-tour/popup-end';
import OnboardingGuidedTourPopupStartComponent from 'webapp/components/onboarding/guided-tour/popup-start';
import SourceTypeInfoPopupComponent from 'webapp/components/source/popup-source-type-info';
import UserPopupForbiddenActionComponent from 'webapp/components/user/popup-forbidden-action';

import type { Metric } from 'webapp/models-custom';
import type Popup from 'webapp/models-custom/popup/base';
import type PopupModal from 'webapp/models-custom/popup/modal';
import type Tour from 'webapp/models-custom/tour';

export const enum BannerBottomType {
	NONE = 0,
	TEXT = 1,
	METRICS_COUNT = 2,
}

export interface StepTourData {
	type: BannerBottomType;
	route: string | null;
	isContinueBtnEnabled: boolean;
	isEligibleToSaveIntoState: boolean;
	isFocused?: boolean;
	text?: string;
	tooltipText?: string;
	canBeSkipped?: boolean;
}

interface StepTour {
	id: string;
	stepData: StepTourData[];
}

interface Step {
	id: number;
	title: string;
	icon: IconName;
	tour: StepTour;
}

// where a guided onboarding tour was left, kept in localStorage to resume it
interface GuidedOnboardingState {
	stepId: number;
	tourStepId: number;
}

export default class FlowTourOnboardingService extends EmberService {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('tour') declare tourService: Service.Tour;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;

	@tracked isBannerShown = false;

	@tracked stepId = 0;
	@tracked tourStepId = 0;
	@tracked activeTour: Tour | null = null;

	minSelectedMetricsCount = 3;
	@tracked private selectedMetrics: string[] = [];
	// Selections toggle, so reaching the count again after an unselect must not advance the tour twice.
	private hasReachedSelectedMetricsCount = false;

	@cached
	get filteredPopularMetrics() {
		return this.mmMetricsService.metricsByPopularity.filter((metric: Metric) => {
			return (
				metric.isOnSource &&
				metric.sourceType?.category === SourceTypeCategory.CLOUD &&
				metric.source?.statusLevel === SourceStatusLevel.ACTIVE &&
				!metric.isGeneric &&
				!metric.mustSelectDimension &&
				!metric.hasUnits
			);
		});
	}

	get isEligibleForGuidedOnboardingTour(): boolean {
		return (
			window.innerWidth > 992 &&
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			!this.currentUserService.isAgencyUserInClientSpace &&
			!this.accountService.isClient &&
			!this.currentUserService.isViewer
		);
	}

	get guidedOnboardingTourButtonStatus(): { enabled: boolean; error: string } {
		if (!this.billingService.checkIfCan(BillingFeature.SOURCES)) {
			return {
				enabled: false,
				error: 'To connect more sources, please upgrade your plan.',
			};
		}

		const isAnyCloudSourceConnected = !!this.mmSourcesService.connectedCloudSourceModels.length;
		if (!isAnyCloudSourceConnected) {
			return {
				enabled: false,
				error: 'Connect a source with pre-defined metrics to start tour',
			};
		}

		if (!this.billingService.checkIfCan(BillingFeature.BOARDS)) {
			return {
				enabled: false,
				error: `You're at your current dashboard limit. To create more dashboards, you need to upgrade your plan`,
			};
		}

		return {
			enabled: true,
			error: '',
		};
	}

	get hasSeenGuidedOnboardingPopupStart(): boolean {
		return this.userSettingsService.hasSeenGuidedOnboardingPopupStart;
	}

	@cached
	get steps(): Step[] {
		let thirdTour;

		if (this.appNotificationsService.connectSelections.length) {
			thirdTour = {
				id: TourId.GUIDED_ONBOARDING__DATA_MANAGER_SELECTED,
				stepData: [
					{
						type: BannerBottomType.NONE,
						route: 'datamanager.available.index',
						isContinueBtnEnabled: false,
						isEligibleToSaveIntoState: true,
						isFocused: false,
					},
				],
			};
		} else {
			thirdTour = {
				id: TourId.GUIDED_ONBOARDING__DATA_MANAGER_POPULAR,
				stepData: [
					{
						type: BannerBottomType.NONE,
						route: 'datamanager.available.index',
						isContinueBtnEnabled: false,
						isEligibleToSaveIntoState: true,
						isFocused: false,
					},
				],
			};
		}

		const skipSelectMetricsStep = !this.filteredPopularMetrics.length;

		const selectedMetricsCount = this.selectedMetricsCount,
			skipStepAfterMetricsSelection =
				!!selectedMetricsCount && selectedMetricsCount < this.minSelectedMetricsCount;

		return [
			{
				id: 1,
				title: 'Select metrics',
				icon: 'Metrics',
				tour: {
					id: TourId.GUIDED_ONBOARDING__METRICS,
					stepData: [
						{
							type: BannerBottomType.METRICS_COUNT,
							route: 'metric-library.index',
							tooltipText: 'Select metrics to continue',
							isContinueBtnEnabled: false,
							isEligibleToSaveIntoState: true,
							isFocused: false,
							canBeSkipped: skipSelectMetricsStep,
						},
						{
							type: BannerBottomType.TEXT,
							route: 'metric-library.index',
							text: 'Great! Now, we are moving to dashboard to visualize these metrics...',
							isContinueBtnEnabled: true,
							isEligibleToSaveIntoState: false,
							isFocused: true,
							canBeSkipped: skipStepAfterMetricsSelection,
						},
					],
				},
			},
			{
				id: 2,
				title: 'Build a dashboard',
				icon: 'Databoards',
				tour: {
					id: TourId.GUIDED_ONBOARDING__WIZARD,
					stepData: [
						{
							type: BannerBottomType.NONE,
							route: 'boards.index',
							isContinueBtnEnabled: false,
							isEligibleToSaveIntoState: true,
							isFocused: false,
							canBeSkipped: this.mmBoardsService.normalBoardModels.length === 0,
						},
						{
							type: BannerBottomType.NONE,
							route: 'boards.index', // need to be defined because of canBeSkipped
							isContinueBtnEnabled: false,
							isEligibleToSaveIntoState: false,
							isFocused: false,
						},
						{
							type: BannerBottomType.TEXT,
							route: 'wizard.source',
							text: 'Click on the connected tool to continue',
							tooltipText: 'To continue, select an integration',
							isContinueBtnEnabled: false,
							isEligibleToSaveIntoState: true,
							isFocused: false,
						},
						{
							type: BannerBottomType.TEXT,
							route: 'wizard.source',
							text: 'Great! Next, we are going to select metrics to add to dashboard...',
							isContinueBtnEnabled: true,
							isEligibleToSaveIntoState: false,
							isFocused: true,
						},
						{
							type: BannerBottomType.METRICS_COUNT,
							route: 'wizard.metrics',
							tooltipText: 'Select metrics to continue',
							isContinueBtnEnabled: false,
							isEligibleToSaveIntoState: false,
							isFocused: false,
						},
						{
							type: BannerBottomType.METRICS_COUNT,
							route: 'wizard.metrics',
							isContinueBtnEnabled: true,
							isEligibleToSaveIntoState: false,
							isFocused: true,
							canBeSkipped: skipStepAfterMetricsSelection,
						},
						{
							type: BannerBottomType.NONE,
							route: 'wizard.connect',
							isContinueBtnEnabled: false,
							isEligibleToSaveIntoState: false,
							isFocused: false,
						},
						{
							type: BannerBottomType.TEXT,
							text: `Here's a preview of your dashboard!<br><span>But the real power is that you can visualize metrics from 2+ sources</span>`,
							route: null, // redirect already happen while creating board in wizard controller via `goToBoardsAndOpenPreview`
							isContinueBtnEnabled: true,
							isEligibleToSaveIntoState: false,
							isFocused: true,
						},
					],
				},
			},
			{
				id: 3,
				title: 'Connect more tools',
				icon: 'Integrations',
				tour: thirdTour,
			},
		];
	}

	get currentStepTour(): StepTour | null {
		return this.steps?.[this.stepId]?.tour || null;
	}

	get currentStepTourSteps(): StepTourData[] {
		return this.currentStepTour?.stepData || [];
	}

	get currentStepTourStepData(): StepTourData | null {
		return this.currentStepTourSteps[this.tourStepId] || null;
	}

	get selectedMetricsCount(): number {
		return this.selectedMetrics.length;
	}

	private get stateLocalStorageKey(): string {
		return `guided-onboarding-${this.currentUserService.id}`;
	}

	getState(): GuidedOnboardingState | null {
		let state: GuidedOnboardingState | null = null;
		let stateStringifiedJSON: string | null = null;

		try {
			stateStringifiedJSON = localStorage.getItem(this.stateLocalStorageKey);
		} catch (_e) {
			// just ignore, not important enough to warn user
		}

		if (stateStringifiedJSON) {
			state = JSON.parse(stateStringifiedJSON) as GuidedOnboardingState;
		}

		return state;
	}

	saveState(state: GuidedOnboardingState): void {
		try {
			localStorage.setItem(this.stateLocalStorageKey, JSON.stringify(state));
		} catch (_e) {
			// just ignore, not important enough to warn user
		}
	}

	addModalToAutomatedPopupQueue(): void {
		this.automatedPopupQueueService.queuePopup({
			popup: this.createOnboardingTourStartPopup(),
			routeWhitelist: ['index'],
			checkAddConditions: () => {
				return !this.hasSeenGuidedOnboardingPopupStart;
			},
			checkStartConditions: () => {
				return this.guidedOnboardingTourButtonStatus.enabled;
			},
		});
	}

	private cleanupState(): void {
		try {
			localStorage.removeItem(this.stateLocalStorageKey);
		} catch (_e) {
			// just ignore, not important enough to warn user
		}
	}

	private showBanner(): void {
		this.isBannerShown = true;
		document.body.classList.add('guided-onboarding');
	}

	private hideBanner(): void {
		this.isBannerShown = false;
		document.body.classList.remove('guided-onboarding');

		this.unfocusBanner();
	}

	private focusBanner(): void {
		document.body.classList.add('guided-onboarding--banner-focused');
	}

	private unfocusBanner(): void {
		document.body.classList.remove('guided-onboarding--banner-focused');
	}

	_popstateFn: (() => void) | null = null;

	startTour(afterInitialRequests = false) {
		const initTour = () => {
			this.showBanner();

			if (!this._popstateFn) {
				this._popstateFn = () => {
					if (!window.location.hash) {
						this.endTour();
					}
				};

				window.addEventListener('popstate', this._popstateFn);
			}
		};

		if (afterInitialRequests) {
			if (!this.isEligibleForGuidedOnboardingTour) {
				return;
			}

			if (this.popupService.anyPopupsOpen) {
				return; // in case some other popup is triggered before ...
			}

			const state = this.getState();
			if (!state) {
				return;
			}

			const { stepId, tourStepId } = state;

			initTour();
			this.stepId = stepId;
			this.tourStepId = tourStepId;

			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			this.filteredPopularMetrics; // call it, just to initialize it
		} else {
			initTour();
		}

		this.continueTour();
	}

	continueTour() {
		const activeTour = this.activeTour,
			currentStepTourStepData = this.currentStepTourStepData;

		if (this.activeTour && this.tourStepId === this.activeTour.stepData.length - 1) {
			this.analyticsService.event(TrackingEvent.GUIDED_ONBOARDING_TOUR_STEP_COMPLETED, {
				level: `step_${this.stepId + 1}`,
			});
		}

		const checkIfStepCanBeSkipped = (tourSteps: StepTourData[], index: number) => {
			if (index === tourSteps.length - 1) {
				return;
			}

			if (tourSteps[index]?.canBeSkipped) {
				this.tourStepId++;
			} else {
				return;
			}

			checkIfStepCanBeSkipped(tourSteps, index + 1);
		};

		const checkIfBannerIsFocused = () => {
			if (this.currentStepTourStepData?.isFocused) {
				this.focusBanner();
			} else {
				this.unfocusBanner();
			}
		};

		if (activeTour && this.tourStepId < activeTour.stepData.length - 1) {
			this.tourStepId++;

			checkIfStepCanBeSkipped(this.currentStepTourSteps, this.tourStepId);
			checkIfBannerIsFocused();

			const goToNextStep = () => {
				schedule('afterRender', this, () => {
					later(() => {
						next(() => {
							activeTour.goToStep(this.tourStepId + 1);
						});
					}, 300);
				});
			};

			const route = this.currentStepTourStepData?.route;
			if (route) {
				this.navigationService.transitionToRoute(route).then(() => {
					goToNextStep();
				});
			} else {
				goToNextStep();
			}
		} else {
			if (activeTour) {
				this.stepId++;
				this.tourStepId = 0;
				this.selectedMetrics = [];
				this.hasReachedSelectedMetricsCount = false;
			}

			checkIfStepCanBeSkipped(this.currentStepTourSteps, this.tourStepId);
			checkIfBannerIsFocused();

			if (this.currentStepTour) {
				const tour = this.tourService.initTour(this.currentStepTour.id)!;
				this.activeTour = tour;

				const startTour = () => {
					schedule('afterRender', this, () => {
						later(() => {
							next(() => {
								tour.start(this.tourStepId + 1);
							});
						}, 300);
					});
				};

				const route = this.currentStepTourStepData?.route;
				if (route) {
					this.navigationService.transitionToRoute(route).then(() => {
						startTour();
					});
				} else {
					startTour();
				}
			}
		}

		if (this.stepId === this.steps.length) {
			this.endTour();
			this.openOnboardingTourEndPopup();
			return;
		}

		if (currentStepTourStepData?.isEligibleToSaveIntoState) {
			this.saveState({
				stepId: this.stepId,
				tourStepId: 0,
			});
		}
	}

	endTour() {
		this.hideBanner();

		this.stepId = 0;
		this.tourStepId = 0;
		this.selectedMetrics = [];
		this.hasReachedSelectedMetricsCount = false;

		if (this.activeTour) {
			this.activeTour.end();
			this.activeTour = null;
		}

		this.cleanupState();

		if (this._popstateFn) {
			window.removeEventListener('popstate', this._popstateFn);
		}
	}

	selectMetrics(metricKey: string) {
		if (!this.isBannerShown) {
			return;
		}

		if (this.selectedMetrics.includes(metricKey)) {
			this.selectedMetrics = this.selectedMetrics.filter(
				(selectedMetric) => selectedMetric !== metricKey,
			);
		} else {
			this.selectedMetrics = [...this.selectedMetrics, metricKey];
		}

		if (
			!this.hasReachedSelectedMetricsCount &&
			this.selectedMetrics.length === this.minSelectedMetricsCount
		) {
			this.hasReachedSelectedMetricsCount = true;
			this.continueTour();
		}
	}

	markGuidedOnboardingPopupStartSeen(): void {
		void this.userSettingsService.save(
			UserSettingsKey.GUIDED_ONBOARDING_POPUP_START_SEEN,
			true,
		);
	}

	// To prevent any other popup to trigger during the guided onboarding.
	// Whitelist only modal and context (blue bubbles) popups that are used during guided onboarding.
	// a blue bubble's data has its tour id as type
	isEligibleToOpenPopup(popup: Popup, data: { type?: string }) {
		const popupModalsEligibleToOpen = [
			CommonPopupConfirmComponent, // when user wants to close tour via banner
			BoardPopupPreviewComponent, // opened during GuidedOnboardingWizardTour while new board is created via wizard
			SourceTypeInfoPopupComponent, // opened during GuidedOnboardingDataManagerPopularTour
			UserPopupForbiddenActionComponent, // can be shown in source/popup-source-type-info - while mail is not verified and we want teammate to connect source
			DataManagerPopupSelectedConnectionsComponent, // opened during GuidedOnboardingDataManagerSelectedTour
		];

		if (
			popupModalsEligibleToOpen.includes(
				popup.componentInnerClass as (typeof popupModalsEligibleToOpen)[number],
			)
		) {
			return true;
		}

		const tourIds = this.steps.reduce((acc: string[], step: Step) => {
			acc.push(step.tour.id);

			return acc;
		}, []);

		if (
			popup.componentInnerClass === CommonPopupsPopupBlueBubble &&
			!!data.type &&
			tourIds.includes(data.type)
		) {
			return true;
		}

		return false;
	}

	createOnboardingTourStartPopup(): PopupModal {
		return this.popupService.createModal(OnboardingGuidedTourPopupStartComponent, {
			popupClass: 'popup-type-onboarding-tour-start',
		});
	}

	openOnboardingTourEndPopup(): PopupModal {
		const popup = this.popupService.createModal(OnboardingGuidedTourPopupEndComponent, {
			popupClass: 'popup-type-onboarding-tour-end',
		});

		popup.open();

		return popup;
	}

	openOnboardingTourExitWarningPopup(): void {
		const onConfirm = () => {
			this.endTour();
			void this.popupService.closeAll(); // in case we closing tour while board/popup-preview is opened (without close btn)
		};

		const onReject = () => {
			this.activeTour = null;
			this.startTour();
		};

		this.popupService.openConfirmPopup({
			onConfirm,
			onReject,
			title: 'Exit the tour?',
			text: "If now isn't the right time to complete the tour, you can always opt-in later from your setup screen.",
			confirmAction: 'Yes, exit',
			cancelAction: 'No, continue',
			isWarning: true,
		});

		this.isBannerShown = false;
	}
}

declare module '@ember/service' {
	interface Registry {
		'flow/tour/onboarding': FlowTourOnboardingService;
	}
}
